/**
 * `/quote/documents/:id` — Document detail view.
 *
 * Single-document panel: file metadata + status (with live polling while
 * processing) + extracted text chunks once `ready` + a delete action.
 * Linked from the list row in `/quote/documents`.
 */
import { useNavigate, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useDeleteQuoteDocument,
  useQuoteDocument,
  useQuoteDocumentChunks,
} from "@/hooks/use-quote";
import { formatTimestamp } from "@/lib/formatters";

function formatBytes(bytes: number | null): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export function QuoteDocumentDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const doc = useQuoteDocument(id);
  const isReady = doc.data?.status === "ready";
  const chunks = useQuoteDocumentChunks(id, { enabled: isReady });
  const deleteMut = useDeleteQuoteDocument();

  function onDelete() {
    if (!doc.data) return;
    if (!window.confirm(`Delete "${doc.data.file_name}"? This can't be undone.`)) return;
    deleteMut.mutate(doc.data.id, {
      onSuccess: () => navigate("/quote/documents"),
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Quote</p>
        <h1
          className="text-2xl font-extrabold tracking-tight text-gray-900"
          data-testid="quote-doc-detail-heading"
        >
          Document detail
        </h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          <button
            type="button"
            className="underline-offset-2 hover:underline"
            onClick={() => navigate("/quote/documents")}
            data-testid="quote-doc-back-link"
          >
            ← Back to documents
          </button>
        </p>
      </header>

      {doc.isLoading && <Skeleton className="h-32 w-full" data-testid="quote-doc-skeleton" />}

      {doc.error && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-destructive" data-testid="quote-doc-error">
              Couldn't load document: {doc.error.message}
            </p>
          </CardContent>
        </Card>
      )}

      {doc.data && (
        <>
          <Card data-testid="quote-doc-metadata-card">
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle
                    className="break-words"
                    data-testid="quote-doc-filename"
                  >
                    {doc.data.file_name}
                  </CardTitle>
                  <CardDescription>
                    {formatBytes(doc.data.file_size_bytes)} · uploaded{" "}
                    {formatTimestamp(doc.data.created_at)}
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onDelete}
                  disabled={deleteMut.isPending}
                  data-testid="quote-doc-delete-cta"
                >
                  {deleteMut.isPending ? "Deleting…" : "Delete"}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="flex flex-wrap items-center gap-2 pt-0">
              <Badge variant="outline" data-testid="quote-doc-type">
                {doc.data.document_type}
              </Badge>
              <Badge
                variant={doc.data.status === "ready" ? "default" : "outline"}
                data-testid="quote-doc-status"
              >
                {doc.data.status}
              </Badge>
              <span className="text-xs text-muted-foreground" data-testid="quote-doc-mime">
                {doc.data.mime_type}
              </span>
              {doc.data.error_message && (
                <p
                  className="basis-full text-xs text-destructive"
                  data-testid="quote-doc-error-message"
                >
                  {doc.data.error_message}
                </p>
              )}
            </CardContent>
          </Card>

          {(doc.data.status === "pending" || doc.data.status === "processing") && (
            <Card data-testid="quote-doc-processing-card">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">
                  Extracting text and computing embeddings. The page will update
                  automatically when this finishes — usually within 10-30 seconds.
                </p>
              </CardContent>
            </Card>
          )}

          {doc.data.status === "failed" && (
            <Card data-testid="quote-doc-failed-card">
              <CardContent className="pt-6">
                <p className="text-sm text-destructive">
                  Processing failed{doc.data.error_message ? `: ${doc.data.error_message}` : "."}{" "}
                  Re-upload the file or contact support if the error persists.
                </p>
              </CardContent>
            </Card>
          )}

          {isReady && (
            <Card data-testid="quote-doc-chunks-card">
              <CardHeader>
                <CardTitle>Extracted chunks</CardTitle>
                <CardDescription>
                  Text split into RAG-ready segments. The AI grounds quote drafts in
                  these chunks via pgvector similarity search.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {chunks.isLoading && (
                  <div className="flex flex-col gap-2">
                    <Skeleton className="h-16 w-full" data-testid="quote-doc-chunks-skeleton" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                )}
                {chunks.error && (
                  <p
                    className="text-sm text-destructive"
                    data-testid="quote-doc-chunks-error"
                  >
                    Couldn't load chunks: {chunks.error.message}
                  </p>
                )}
                {chunks.data && chunks.data.items.length === 0 && (
                  <p
                    className="text-sm text-muted-foreground"
                    data-testid="quote-doc-chunks-empty"
                  >
                    Document is marked ready but no chunks were produced. This is
                    unusual — re-uploading should fix it.
                  </p>
                )}
                {chunks.data && chunks.data.items.length > 0 && (
                  <>
                    <p
                      className="mb-3 text-xs text-muted-foreground"
                      data-testid="quote-doc-chunks-count"
                    >
                      {chunks.data.items.length}{" "}
                      {chunks.data.items.length === 1 ? "chunk" : "chunks"}
                    </p>
                    <ul className="flex flex-col gap-3" data-testid="quote-doc-chunks-list">
                      {chunks.data.items.map((chunk) => (
                        <li
                          key={chunk.id}
                          className="rounded border border-gray-100 bg-gray-50/50 p-3"
                          data-testid={`quote-doc-chunk-${chunk.chunk_index}`}
                        >
                          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Chunk {chunk.chunk_index}
                          </p>
                          <p className="whitespace-pre-wrap break-words text-sm text-gray-800">
                            {chunk.content}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

export default QuoteDocumentDetailPage;
